import type { App } from '../app/App';
import { RateUsPopup } from '../game/views/popups/RateUsPopup';
import type { RateUs } from '../app/platform/Platform';
import type { PopupStackController } from '../popups/PopupStackController';

/**
 * Opens Rate Us popup for first diorama completion
 */
export async function openRateUsPopupForFirstDiorama(app: App): Promise<void> {
  const {
    controller: { popups },
    platform: { rateUs },
  } = app as {
    controller: { popups: PopupStackController };
    platform: { rateUs: RateUs };
  };

  if (!rateUs) return Promise.resolve();
  await rateUs.init();
  if (!rateUs.isSupported() || !rateUs.shouldShowForFirstDiorama()) {
    return Promise.resolve();
  }

  return openRateUsPopupInternal(app, rateUs, popups, 0);
}

/**
 * Opens Rate Us popup for specific level completion
 */
export async function openRateUsPopupForLevel(app: App, levelNumber: number): Promise<void> {
  const {
    controller: { popups },
    platform: { rateUs },
  } = app as {
    controller: { popups: PopupStackController };
    platform: { rateUs: RateUs };
  };

  if (!rateUs) return Promise.resolve();
  await rateUs.init();
  if (!rateUs.isSupported() || !rateUs.shouldShowForLevel(levelNumber)) {
    return Promise.resolve();
  }

  return openRateUsPopupInternal(app, rateUs, popups, levelNumber);
}

/**
 * Internal function to create and show the Rate Us popup
 * @param levelNumberOrDiorama Level number or 0 for first diorama
 */
function openRateUsPopupInternal(
  _app: App,
  rateUs: RateUs,
  popups: PopupStackController,
  levelNumberOrDiorama: number,
): Promise<void> {
  const popup = new RateUsPopup();

  let resolveClosed: () => void;
  const closedPromise = new Promise<void>((resolve) => {
    resolveClosed = resolve;
  });

  const handleEnjoy = (): void => {
    // Mark as shown for this trigger - user went to rate
    rateUs.markAsShownFor(levelNumberOrDiorama);
    rateUs.onPopupShown();
    rateUs.onEnjoy();
    // Disconnect signals to prevent memory leaks
    popup.onConfirmClicked.disconnectAll();
    popup.onCancelClicked.disconnectAll();
    void popups.closeActivePopup();
  };

  const handleNotReally = (): void => {
    // Mark as shown for this trigger - user made a decision
    const rateUsTyped = rateUs as {
      markAsShownFor: (level: number) => void;
      onPopupShown: () => void;
      onNotReally: () => void;
    };

    rateUsTyped.markAsShownFor(levelNumberOrDiorama);
    rateUsTyped.onPopupShown();
    rateUsTyped.onNotReally();

    // Disconnect signals to prevent memory leaks
    popup.onConfirmClicked.disconnectAll();
    popup.onCancelClicked.disconnectAll();
    void popups.closeActivePopup();
  };

  popup.onConfirmClicked.connect(handleEnjoy);
  popup.onCancelClicked.connect(handleNotReally);

  void popups.addPopup({
    popupInstance: popup,
    settings: { overlayFillOpacity: 0.8 },
    onClosed: () => {
      resolveClosed?.();
    },
  });

  return closedPromise;
}

